import React, { Component, createRef } from 'react';

// CSS
import '../Courses/Courses.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, uploadCourseMedia } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Panel, Confirm } from '../../components/UI/Overlays';
import { EmptyState, TableWait, Chip, Tip } from '../../components/UI/Feedback';
import { Toolbar } from '../../components/UI/Controls';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';

// Properties
const properties = require('../../properties.json');

const CATEGORIES = [
    { label: 'ملفات مجانية للتحميل', value: 'DOWNLOAD' },
    { label: 'فيديوهات تعليمية', value: 'TUTORIAL' },
    { label: 'مقالات', value: 'ARTICLE' },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

// Each category reads as itself: its own glyph in the leading cell and its
// own colour on the chip, so the three kinds are told apart down the column
// without reading a word.
const CATEGORY_LOOK = {
    DOWNLOAD: { icon: 'pi pi-download', kind: 'ok' },
    TUTORIAL: { icon: 'pi pi-video', kind: 'wait' },
    ARTICLE: { icon: 'pi pi-file', kind: 'off' },
};

const EMPTY_FORM = { id: null, title: '', description: '', linkUrl: '', category: 'DOWNLOAD' };

/** Manages the "Resources" page (free downloads / tutorials / articles) -- see
 *  ResourceController on the backend. Kept deliberately simple (4 fields, one optional file
 *  upload) since the whole point was to make this easy for a teacher to fill in themselves. */
class Resources extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        items: [],
        loading: true,
        form: EMPTY_FORM,
        saving: false,
        uploading: false,
        uploadProgress: 0,

        globalFilter: null,

        // The form used to sit permanently above the list, and editing
        // scrolled the page back to the top to reach it. It opens over the
        // list now; `form.id` still decides whether it is adding or editing.
        formOpen: false,


        // the item the teacher asked to delete, held while the confirm is up
        doomed: null,
        deleting: false,
    }

    componentDidMount() {
        document.title = properties.site_name + " | الموارد";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        this.load();
    }


    load = () => {
        this.setState({ loading: true });
        apiFetch("/resources/mine")
            .then(items => this.setState({ items, loading: false }))
            .catch(error => {
                console.error('Error:', error);
                this.setState({ loading: false });
            });
    }

    resetForm = () => this.setState({ form: EMPTY_FORM });

    openAdd = () => {
        this.setState({ form: EMPTY_FORM, formOpen: true });
    }

    closeForm = () => {
        this.setState({ formOpen: false, form: EMPTY_FORM });
    }

    editItem = (item) => {
        this.setState({
            form: { id: item.id, title: item.title || '', description: item.description || '', linkUrl: item.linkUrl || '', category: item.category },
            formOpen: true,
        });
    }

    // The browser's own confirm box is gone; the question is asked in the
    // product's own confirm card instead. The delete behind it is unchanged.
    deleteItem = (item) => {
        this.setState({ deleting: true });
        apiFetch(`/resources/${item.id}`, { method: "DELETE" })
            .then(() => {
                this.setState({ doomed: null, deleting: false });
                this.load();
            })
            .catch(error => {
                console.error('Error:', error);
                this.setState({ doomed: null, deleting: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    uploadFile = async (event) => {
        const file = event.files && event.files[0];
        if (!file) return;
        this.setState({ uploading: true, uploadProgress: 0 });
        try {
            const url = await uploadCourseMedia(file, (pct) => this.setState({ uploadProgress: pct }));
            this.setState(s => ({ form: { ...s.form, linkUrl: url }, uploading: false }));
            this.toast.current.show({ severity: 'info', summary: 'تم بنجاح', detail: 'تم رفع الملف' });
        } catch (error) {
            console.error('Error:', error);
            this.setState({ uploading: false });
            this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'فشل رفع الملف' });
        } finally {
            event.options && event.options.clear && event.options.clear();
        }
    }

    save = () => {
        const { form, items } = this.state;
        if (!form.title || !form.linkUrl) {
            this.toast.current.show({ severity: 'error', summary: 'تنبيه', detail: 'من فضلك أدخل العنوان والرابط أو ارفع ملف' });
            return;
        }
        this.setState({ saving: true });
        const body = {
            title: form.title,
            description: form.description,
            linkUrl: form.linkUrl,
            category: form.category,
            sortOrder: form.id ? undefined : items.length,
        };
        const request = form.id
            ? apiFetch(`/resources/${form.id}`, { method: "PUT", body })
            : apiFetch("/resources", { method: "POST", body });
        request
            .then(() => {
                // the save finished the job, so the panel closes and the list
                // behind it goes and fetches what was just written
                this.setState({ saving: false, form: EMPTY_FORM, formOpen: false });
                this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم الحفظ' });
                this.load();
            })
            .catch(error => {
                console.error('Error:', error);
                this.setState({ saving: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    render() {
        const { items, loading, form, saving, uploading, uploadProgress, doomed, deleting } = this.state;

        // The save stays out of reach until the form can actually be saved --
        // the same two values save() checks, read here so the button can say
        // so before it is pressed rather than after.
        const canSave = !!(form.title && form.title.trim() && form.linkUrl && form.linkUrl.trim());

        // What a link actually points at, read in two lines: an uploaded
        // file is its own name over where it lives, a site is its host over
        // the page inside it. Either way the whole address is on the cell.
        const linkInfo = (url) => {
            let host = '';
            let last = '';
            let parsed = true;

            try {
                const u = new URL(url, window.location.origin);
                host = u.hostname;
                last = decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() || '');
            } catch (e) {
                parsed = false;
                last = url;
            }

            const isFile = /\.[a-z0-9]{2,5}$/i.test(last);
            const mine = parsed && host === window.location.hostname;

            return {
                icon: isFile ? 'pi pi-paperclip' : 'pi pi-external-link',
                name: isFile ? last : host || url,
                sub: isFile ? (mine ? 'ملف مرفوع' : host) : last || 'رابط خارجي',
            };
        };

        const titleBody = (row) => {
            const look = CATEGORY_LOOK[row.category] || CATEGORY_LOOK.ARTICLE;
            return (
                <span className="ui-row-lead">
                    <span className="thumb is-empty" aria-hidden="true">
                        <i className={look.icon}></i>
                    </span>
                    <span className="tx">
                        <b>{row.title}</b>
                        <small>{row.description || 'من غير وصف'}</small>
                    </span>
                </span>
            );
        };

        const categoryBody = (row) => {
            const look = CATEGORY_LOOK[row.category] || CATEGORY_LOOK.ARTICLE;
            return <Chip kind={look.kind}>{CATEGORY_LABEL[row.category] || row.category}</Chip>;
        };

        const linkBody = (row) => {
            if (!row.linkUrl) return <span className="ui-bits is-none">مفيش رابط</span>;

            const info = linkInfo(row.linkUrl);
            return (
                <a
                    className="ui-linkcell"
                    href={row.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.linkUrl}
                >
                    <i className={info.icon}></i>
                    <span className="tx">
                        <b dir="ltr">{info.name}</b>
                        <small dir="ltr">{info.sub}</small>
                    </span>
                    <i className="go pi pi-angle-left" aria-hidden="true"></i>
                </a>
            );
        };

        const actionBodyTemplate = (row) => (
            <React.Fragment>
                <Tip text="تعديل">
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-secondary p-ml-2" onClick={() => this.editItem(row)} />
                </Tip>
                <Tip text="حذف">
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => this.setState({ doomed: row })} />
                </Tip>
            </React.Fragment>
        );

        const saveButton = (
            <Button
                className={`add-button ${saving ? "loading" : ""}`}
                label={saving ? "جاري الحفظ..." : (form.id ? "حفظ التعديلات" : "إضافة")}
                disabled={!canSave || saving}
                onClick={this.save}
            />
        );

        return (
            <>
                <Header
                    pageName="الموارد"
                    action={{ label: "إضافة مورد", icon: "pi pi-plus", onClick: this.openAdd }}
                />
                <Toast ref={this.toast}></Toast>

                {/* Adding and editing are the same form; `form.id` is the only
                    thing that differs, exactly as it always did. */}
                <Panel
                    visible={this.state.formOpen}
                    onHide={this.closeForm}
                    title={form.id ? "تعديل مورد" : "إضافة مورد"}
                    subtitle="البيانات دي هتظهر في صفحة الموارد على موقعك الخارجي"
                    actions={saveButton}
                >
                    <div className="add-lesson-page in-panel">
                        <div className="content">
                            <div className="container">
                                <section className="ui-sec" style={{ "--d": "0ms" }}>
                                    <h3>
                                        <span className="n">١</span>
                                        <span className="t">
                                            المورد
                                            <small>التصنيف بيحدد تحت أي قسم هيظهر في الموقع</small>
                                        </span>
                                    </h3>
                                    <div className="p-grid p-fluid">
                                        <div className="p-col-12 p-md-4">
                                            <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <Dropdown
                                                        value={form.category}
                                                        options={CATEGORIES}
                                                        placeholder="اختر"
                                                        onChange={(e) => this.setState({ form: { ...form, category: e.value } })}
                                                    />
                                                    <label>التصنيف</label>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-col-12 p-md-4">
                                            <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <InputText
                                                        id="resource-title"
                                                        value={form.title}
                                                        onChange={(e) => this.setState({ form: { ...form, title: e.target.value } })}
                                                    />
                                                    <label htmlFor="resource-title">العنوان</label>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-col-12 p-md-12">
                                            <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <InputTextarea
                                                        id="resource-desc"
                                                        rows={2}
                                                        value={form.description}
                                                        onChange={(e) => this.setState({ form: { ...form, description: e.target.value } })}
                                                    />
                                                    <label htmlFor="resource-desc">
                                                        وصف مختصر
                                                        <small className="ui-fieldhint">اختياري — سطر بيشرح المورد لطلابك</small>
                                                    </label>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section className="ui-sec" style={{ "--d": "70ms" }}>
                                    <h3>
                                        <span className="n">٢</span>
                                        <span className="t">
                                            الملف أو الرابط
                                            <small>ارفع ملف من جهازك، أو حطّ لينك خارجي</small>
                                        </span>
                                    </h3>
                                    <div className="p-grid p-fluid">
                                        <div className="p-col-12 p-md-12">
                                            <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <InputText
                                                        id="resource-link"
                                                        value={form.linkUrl}
                                                        onChange={(e) => this.setState({ form: { ...form, linkUrl: e.target.value } })}
                                                        placeholder="https://..."
                                                    />
                                                    <label htmlFor="resource-link">الرابط</label>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-col-12 p-md-12">
                                            <FileUpload
                                                name="resourceFile"
                                                customUpload
                                                uploadHandler={this.uploadFile}
                                                auto
                                                disabled={uploading}
                                                multiple={false}
                                                maxFileSize={200000000}
                                                chooseLabel={uploading ? "جاري الرفع..." : "أو ارفع ملف"}
                                                uploadLabel="رفع"
                                                cancelLabel="إلغاء"
                                                mode="basic"
                                            />
                                        </div>
                                        {uploading && (
                                            <div className="p-col-12">
                                                <div className="upload-progress-row">
                                                    <ProgressBar value={uploadProgress} style={{ flex: 1, height: '10px' }} />
                                                    <span>{uploadProgress}%</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </Panel>

                <Confirm
                    visible={!!doomed}
                    onHide={() => this.setState({ doomed: null })}
                    name={doomed ? doomed.title : ""}
                    note="المورد هيتشال من صفحة الموارد على موقعك، ومش هينفع ترجع عنه."
                    busy={deleting}
                    onConfirm={() => this.deleteItem(doomed)}
                />

                <div className="content">
                    <div className="container">
                        <div className="card">
                            <Card>
                                <Toolbar
                                    value={this.state.globalFilter || ''}
                                    onSearch={(v) => this.setState({ globalFilter: v })}
                                    placeholder="بحث في الموارد"
                                    count={loading ? null : `${items.length} مورد`}
                                />

                                <DataTable
                                    sortable
                                    globalFilter={this.state.globalFilter}
                                    className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                                    value={items}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[10, 20, 50]}
                                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                                    emptyMessage={
                                        loading ? (
                                            <TableWait />
                                        ) : (
                                            <EmptyState
                                                title="لسه مفيش موارد"
                                                hint="ضيف أول ملف أو فيديو أو مقال يظهر لطلابك على موقعك"
                                                icon="pi pi-folder-open"
                                                action={{ label: "إضافة مورد", onClick: this.openAdd }}
                                            />
                                        )
                                    }
                                >
                                    <Column sortable field="title" body={titleBody} header="المورد"></Column>
                                    <Column sortable field="category" body={categoryBody} header="التصنيف"></Column>
                                    <Column field="linkUrl" body={linkBody} header="الرابط"></Column>
                                    <Column header="تعديل / حذف" body={actionBodyTemplate}></Column>
                                </DataTable>
                            </Card>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default Resources;
